import React, { useState } from 'react';

const ReporteEgresoStats = ({ reporteEgreso = [], tipoMovTexto = 'Egreso' }) => {
    const [modoTop, setModoTop] = useState('monto');
    const [productoSeleccionadoKey, setProductoSeleccionadoKey] = useState(null);
    if (!Array.isArray(reporteEgreso) || reporteEgreso.length === 0) return null;

    // KPIs principales
    const totalMovimientos = reporteEgreso.length;
    const totalMonto = reporteEgreso.reduce((acc, x) => acc + parseFloat(x.Total || 0), 0);
    const totalUnidades = reporteEgreso.reduce((acc, x) => acc + parseFloat(x.Cantidad || 0), 0);
    const promedio = totalMovimientos > 0 ? totalMonto / totalMovimientos : 0;
    const documentosUnicos = new Set(reporteEgreso.map(x => x.Numero)).size;

    // Resumen por tipo de transacción
    const porTipo = Object.values(
        reporteEgreso.reduce((acc, x) => {
            const k = x.TipoTransaccion || 'SIN TIPO';
            if (!acc[k]) acc[k] = { tipo: k, movimientos: 0, cantidad: 0, monto: 0 };
            acc[k].movimientos += 1;
            acc[k].cantidad += parseFloat(x.Cantidad || 0);
            acc[k].monto += parseFloat(x.Total || 0);
            return acc;
        }, {})
    ).sort((a, b) => b.monto - a.monto);

    // Resumen de productos
    const resumenProductos = Object.values(
        reporteEgreso.reduce((acc, x) => {
            const k = `${x.Producto}__${x.Presentacion}`;
            if (!acc[k]) {
                acc[k] = {
                    key: k,
                    producto: x.Producto || 'SIN PRODUCTO',
                    presentacion: x.Presentacion || 'SIN PRESENTACIÓN',
                    cantidad: 0,
                    monto: 0,
                    movimientos: 0,
                    tiposTransaccion: new Set(),
                    movimientosDetalle: []
                };
            }
            acc[k].cantidad += parseFloat(x.Cantidad || 0);
            acc[k].monto += parseFloat(x.Total || 0);
            acc[k].movimientos += 1;
            acc[k].tiposTransaccion.add(x.TipoTransaccion || 'SIN TIPO');
            acc[k].movimientosDetalle.push({
                numero: x.Numero || '-',
                fechaMovimiento: x.FechaMovimiento || null,
                tipoTransaccion: x.TipoTransaccion || 'SIN TIPO',
                cantidad: parseFloat(x.Cantidad || 0),
                monto: parseFloat(x.Total || 0),
                usuario: x.Usuario || '-'
            });
            return acc;
        }, {})
    ).map(item => ({
        ...item,
        tiposTransaccion: Array.from(item.tiposTransaccion),
        movimientosDetalle: item.movimientosDetalle.sort((a, b) => {
            if (!a.fechaMovimiento && !b.fechaMovimiento) return 0;
            if (!a.fechaMovimiento) return 1;
            if (!b.fechaMovimiento) return -1;
            return new Date(b.fechaMovimiento) - new Date(a.fechaMovimiento);
        })
    }));

    const productosMap = resumenProductos.reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
    }, {});

    const topProductos = [...resumenProductos]
        .sort((a, b) => b.monto - a.monto)
        .slice(0, 5);

    const topProductosUnidades = [...resumenProductos]
        .sort((a, b) => b.cantidad - a.cantidad)
        .slice(0, 5);

    const maxMontoTipo = Math.max(...porTipo.map(t => t.monto), 1);
    const maxMontoProd = Math.max(...topProductos.map(p => p.monto), 1);
    const maxCantidadProd = Math.max(...topProductosUnidades.map(p => p.cantidad), 1);
    const fmt = n => `S/ ${parseFloat(n).toFixed(2)}`;
    const fmtQ = n => Number.isInteger(n) ? n : parseFloat(n).toFixed(2);

    const topConfig = {
        monto: {
            title: `Top 5 productos por Monto ${tipoMovTexto}`,
            data: topProductos,
            progressValue: (item) => item.monto,
            progressMax: maxMontoProd,
            valueLabel: (item) => fmt(item.monto)
        },
        unidades: {
            title: `Top 5 productos por Unidades ${tipoMovTexto}`,
            data: topProductosUnidades,
            progressValue: (item) => item.cantidad,
            progressMax: maxCantidadProd,
            valueLabel: (item) => fmtQ(item.cantidad)
        }
    };

    const topActual = topConfig[modoTop];
    const productoDetalle = productoSeleccionadoKey ? productosMap[productoSeleccionadoKey] : null;

    const kpis = [
        {
            label: 'Movimientos registrados',
            value: totalMovimientos,
            sub: `${documentosUnicos} documento${documentosUnicos !== 1 ? 's' : ''} únicos`,
            accent: '#1D9E75',
        },
        {
            label: 'Monto total',
            value: fmt(totalMonto),
            sub: `Prom. ${fmt(promedio)} / mov.`,
            accent: '#185FA5',
        },
        {
            label: 'Unidades totales',
            value: fmtQ(totalUnidades),
            sub: `${(totalUnidades / totalMovimientos).toFixed(1)} und. / mov.`,
            accent: '#BA7517',
        },
        {
            label: 'Tipos de transacción',
            value: porTipo.length,
            sub: porTipo[0] ? `Mayor: ${porTipo[0].tipo.split(' ').slice(0, 2).join(' ')}` : '',
            accent: '#993C1D',
        },
    ];

    return (
        <div className="container mt-3 mb-3">
            <div className="row">
                {kpis.map((kpi, i) => (
                    <div key={i} className="col-12 col-md-6 col-lg-3 mb-3">
                        <div className="card h-100" style={{ background: 'rgba(255, 255, 255, 0.22)' }}>
                            <div className="card-header text-white" style={{ background: '#35605a' }}>
                                <h6 className="mb-0">{kpi.label}</h6>
                            </div>
                            <div className="card-body">
                                <h4 className="mb-1" style={{ color: kpi.accent }}>{kpi.value}</h4>
                                <small className="text-muted">{kpi.sub}</small>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row">
                <div className="col-12 col-lg-6 mb-3">
                    <div className="card" style={{ background: 'rgba(255, 255, 255, 0.22)' }}>
                        <div className="card-header d-flex justify-content-between text-white align-items-center" style={{ background: '#35605a' }}>
                            <h6 className="mb-0">Resumen por Tipo de Transacción</h6>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th className="text-right">Cant.</th>
                                            <th className="text-right">Monto</th>
                                            <th className="text-center">%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {porTipo.map((t, i) => {
                                            const porcentaje = totalMonto > 0 ? (t.monto / totalMonto) * 100 : 0;
                                            return (
                                                <tr key={i}>
                                                    <td>
                                                        <strong>{t.tipo}</strong>
                                                        <div className="progress mt-1" style={{ height: '6px' }}>
                                                            <div
                                                                className="progress-bar"
                                                                style={{
                                                                    width: `${(t.monto / maxMontoTipo * 100).toFixed(0)}%`,
                                                                    backgroundColor: '#01a59c'
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-right">{fmtQ(t.cantidad)}</td>
                                                    <td className="text-right">{fmt(t.monto)}</td>
                                                    <td className="text-center">
                                                        <span className="badge text-white" style={{backgroundColor:'#01a59c'}}>{porcentaje.toFixed(1)}%</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-6 mb-3">
                    <div className="card" style={{ background: 'rgba(255, 255, 255, 0.22)' }}>
                        <div className="card-header d-flex justify-content-between text-white align-items-center" style={{ background: '#35605a' }}>
                            <h6 className="mb-0">{topActual.title}</h6>
                            <div className="btn-group btn-group-sm">
                                <button
                                    type="button"
                                    className={`btn ${modoTop === 'monto' ? 'btn-light' : 'btn-outline-light'}`}
                                    onClick={() => setModoTop('monto')}
                                >
                                    Monto
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${modoTop === 'unidades' ? 'btn-light' : 'btn-outline-light'}`}
                                    onClick={() => setModoTop('unidades')}
                                >
                                    Unidades
                                </button>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Producto</th>
                                            <th className="text-right">Und.</th>
                                            <th className="text-right">Monto</th>
                                            <th className="text-center">Detalle</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topActual.data.map((p, i) => (
                                            <tr key={`${modoTop}_${i}`}>
                                                <td className="text-center"><span className="badge badge-secondary">{i + 1}</span></td>
                                                <td>
                                                    <strong>{p.producto}</strong>
                                                    <div className="text-muted" style={{ fontSize: '12px' }}>{p.presentacion}</div>
                                                </td>
                                                <td className="text-right">{fmtQ(p.cantidad)}</td>
                                                <td className="text-right">{fmt(p.monto)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => setProductoSeleccionadoKey(p.key)}
                                                        title="Ver detalles"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {productoDetalle && (
                <div className="row">
                    <div className="col-12 mb-3">
                        <div className="card shadow-sm">
                            <div className="card-header text-white d-flex justify-content-between align-items-center" style={{ background: '#35605a' }}>
                                <h6 className="mb-0">Detalle del Producto Seleccionado</h6>
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={() => setProductoSeleccionadoKey(null)}
                                >
                                    <i className="fas fa-times"></i> Cerrar
                                </button>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12 col-md-6 mb-2">
                                        <strong>Producto:</strong> {productoDetalle.producto}
                                    </div>
                                    <div className="col-12 col-md-6 mb-2">
                                        <strong>Presentación:</strong> {productoDetalle.presentacion}
                                    </div>
                                    <div className="col-12 col-md-4 mb-2">
                                        <strong>Unidades:</strong> {fmtQ(productoDetalle.cantidad)}
                                    </div>
                                    <div className="col-12 col-md-4 mb-2">
                                        <strong>Monto total:</strong> {fmt(productoDetalle.monto)}
                                    </div>
                                    <div className="col-12 col-md-4 mb-2">
                                        <strong>Movimientos:</strong> {productoDetalle.movimientos}
                                    </div>
                                    <div className="col-12">
                                        <strong>Tipos transacción:</strong>{' '}
                                        {productoDetalle.tiposTransaccion.map((tipo, idx) => (
                                            <span key={idx} className="badge badge-info mr-1">{tipo}</span>
                                        ))}
                                    </div>
                                </div>

                                <hr />
                                <h6 className="mb-2">Movimientos usados en el ranking</h6>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>Nro Movimiento</th>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th className="text-right">Cantidad</th>
                                                <th className="text-right">P. Unitario</th>
                                                <th className="text-right">Monto</th>
                                                <th>Usuario</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productoDetalle.movimientosDetalle.map((mov, idx) => (
                                                <tr key={`${productoDetalle.key}_mov_${idx}`}>
                                                    <td>{mov.numero}</td>
                                                    <td>{mov.fechaMovimiento ? new Date(mov.fechaMovimiento).toLocaleString() : '-'}</td>
                                                    <td>{mov.tipoTransaccion}</td>
                                                    <td className="text-right">{fmtQ(mov.cantidad)}</td>
                                                    <td className="text-right">{fmt(mov.monto / mov.cantidad)}</td>
                                                    <td className="text-right">{fmt(mov.monto)}</td>
                                                    <td>{mov.usuario}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-2">
                                    <small className="text-muted">Comparativo visual según criterio seleccionado ({modoTop}).</small>
                                    <div className="progress mt-1" style={{ height: '8px' }}>
                                        <div
                                            className="progress-bar"
                                            style={{
                                                width: `${(
                                                    (modoTop === 'monto' ? productoDetalle.monto : productoDetalle.cantidad) /
                                                    (modoTop === 'monto' ? maxMontoProd : maxCantidadProd)
                                                ) * 100}%`,
                                                backgroundColor: '#01a59c'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReporteEgresoStats;